import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, RotateCcw, LogOut, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { proApi, eur, errMsg } from "@/lib/proApi";
import { usePro } from "@/context/ProContext";
import { PendingNotice } from "@/pages/pro/ProLayout";
import { ProfileFields, profilePayload } from "@/pages/pro/ProAuth";
import { BankBox } from "@/pages/pro/ProCheckout";
import { ProQuickReorder } from "@/components/ProQuickReorder";
import { ProMyCodeCard } from "@/components/ProMyCode";

const STATUS_STYLE = { pending_payment: "bg-amber-50 text-amber-900", paid: "bg-blue-50 text-blue-800", preparing: "bg-amber-50 text-amber-800", shipped: "bg-indigo-50 text-indigo-800",
  delivered: "bg-green-50 text-green-800", cancelled: "bg-gray-100 text-gray-700", refunded: "bg-rose-50 text-rose-800" };

const Orders = () => {
  const { approved, loadCart, user } = usePro();
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);
  useEffect(() => { proApi.get("/pro/orders").then(({ data }) => setOrders(data)); }, []);
  const reorder = async (n) => {
    try {
      const { data } = await proApi.post(`/pro/orders/${n}/reorder`);
      await loadCart(user);
      toast.success(`${data.added} référence(s) ajoutée(s) au panier${data.skipped ? `, ${data.skipped} indisponible(s)` : ""}`);
      nav("/espace-pro/panier");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!orders) return null;
  if (!orders.length) return <p data-testid="pro-orders-empty" className="text-sm text-[var(--oz-muted)]">Aucune commande pour le moment.</p>;
  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.order_number} data-testid={`pro-order-${o.order_number}`} className="rounded-2xl bg-white border border-[var(--oz-border)] p-5">
          <div className="flex flex-wrap justify-between gap-2">
            <div>
              <p className="font-semibold">{o.order_number}</p>
              <p className="text-xs text-[var(--oz-muted)]">{new Date(o.created_at).toLocaleDateString("fr-FR")} • {{ carrier: "Transporteur", pickup: "Retrait au dépôt" }[o.shipping.method] || "Mondial Relay"}{o.tracking_number ? ` • Suivi ${o.tracking_number}` : ""}</p>
            </div>
            <span data-testid={`pro-order-status-${o.order_number}`} className={`self-start text-xs font-semibold px-3 py-1 rounded-full ${STATUS_STYLE[o.status] || ""}`}>{o.status_label}</span>
          </div>
          {o.bank && <BankBox o={o} />}
          {o.quote_number && <p className="mt-2 text-xs text-[var(--oz-muted)]">Selon devis {o.quote_number}</p>}
          <ul className="mt-3 text-sm text-[var(--oz-muted)]">
            {o.lines.map((l) => <li key={`${l.slug}-${l.pack_size}`}>{l.name} — {l.lots} × lot de {l.pack_size}</li>)}
          </ul>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm"><strong>{eur(o.total_ttc_cents)} TTC</strong> <span className="text-[var(--oz-muted)]">({eur(o.total_ht_cents)} HT)</span></p>
            <div className="flex flex-wrap gap-2">
              {o.invoice && <a href={o.invoice.url} target="_blank" rel="noopener noreferrer" data-testid={`pro-invoice-${o.order_number}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[var(--oz-border)] text-xs font-semibold"><FileText className="w-3.5 h-3.5" /> Facture {o.invoice.number}</a>}
              {o.credit_note && <a href={o.credit_note.url} target="_blank" rel="noopener noreferrer" data-testid={`pro-credit-${o.order_number}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[var(--oz-border)] text-xs font-semibold"><FileText className="w-3.5 h-3.5" /> Avoir {o.credit_note.number}</a>}
              {approved && <button onClick={() => reorder(o.order_number)} data-testid={`pro-reorder-${o.order_number}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[var(--oz-dark)] text-white text-xs font-semibold"><RotateCcw className="w-3.5 h-3.5" /> Commander à nouveau</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Q_STATUS = { valid: "valable", expired: "expiré", ordered: "commandé" };

const Quotes = () => {
  const { approved, loadCart, user } = usePro();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const order = async (n) => {
    setBusy(n);
    try {
      const { data } = await proApi.post(`/pro/quotes/${n}/order`);
      await loadCart(user);
      nav(`/espace-pro/commande?ship=${data.shipping_method}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  useEffect(() => { proApi.get("/pro/quotes").then(({ data }) => setRows(data)); }, []);
  if (!rows) return null;
  if (!rows.length) return <p data-testid="pro-quotes-empty" className="text-sm text-[var(--oz-muted)]">Aucun devis. Créez-en un depuis votre panier.</p>;
  return (
    <div className="space-y-2">
      {rows.map((q) => {
        return (
          <div key={q.number} data-testid={`pro-quote-${q.number}`} className="rounded-2xl bg-white border border-[var(--oz-border)] p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div>
              <p className="font-semibold">{q.number}</p>
              <p className="text-xs text-[var(--oz-muted)]">{new Date(q.created_at).toLocaleDateString("fr-FR")} • <span data-testid={`pro-quote-status-${q.number}`}>{q.status === "valid" ? `valable jusqu'au ${new Date(q.valid_until).toLocaleDateString("fr-FR")}` : `${Q_STATUS[q.status]}${q.order_number ? ` (${q.order_number})` : ""}`}</span></p>
            </div>
            <p><strong>{eur(q.total_ttc_cents)} TTC</strong> <span className="text-[var(--oz-muted)]">({eur(q.total_ht_cents)} HT)</span></p>
            <div className="flex gap-2">
              <a href={q.url} target="_blank" rel="noopener noreferrer" data-testid={`pro-quote-link-${q.number}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[var(--oz-border)] text-xs font-semibold"><FileText className="w-3.5 h-3.5" /> PDF</a>
              {approved && q.status === "valid" && <button onClick={() => order(q.number)} disabled={!!busy} data-testid={`pro-quote-order-${q.number}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] text-xs font-semibold disabled:opacity-60"><ShoppingBag className="w-3.5 h-3.5" /> Commander ce devis</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Profile = () => {
  const { user, setUser, loadCart } = usePro();
  const [f, setF] = useState({ ...user, siret: user.siret || "", vat_number: user.vat_number || "", _same: false });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await proApi.put("/pro/me", profilePayload(f));
      setUser(data); loadCart(data);
      toast.success("Coordonnées enregistrées");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4 rounded-2xl bg-white border border-[var(--oz-border)] p-5 sm:p-6" data-testid="pro-profile-form">
      <p className="text-sm text-[var(--oz-muted)]">E-mail de connexion : <strong>{user.email}</strong></p>
      <ProfileFields f={f} setF={setF} />
      <button disabled={busy} data-testid="pro-profile-save" className="px-6 py-3 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm disabled:opacity-60">Enregistrer</button>
    </form>
  );
};

export default function ProAccount() {
  const { user, approved, signOut } = usePro();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "commandes";
  const nav = useNavigate();
  return (
    <div data-testid="pro-account" className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[var(--oz-text)]">Mon compte pro</h1>
          <p className="mt-1 text-sm text-[var(--oz-muted)]">{user.company_name} — {user.email}</p>
        </div>
        <button onClick={() => { signOut(); nav("/espace-pro"); }} data-testid="pro-logout" className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)]"><LogOut className="w-4 h-4" /> Se déconnecter</button>
      </div>
      {!approved && <div className="mt-6"><PendingNotice /></div>}
      <ProMyCodeCard className="mt-6" />
      <ProQuickReorder className="mt-6" />
      <div className="mt-8 flex flex-wrap gap-2">
        {[["commandes", "Commandes et factures"], ["devis", "Devis"], ["profil", "Coordonnées"]].map(([k, t]) => (
          <button key={k} onClick={() => setSp({ tab: k })} data-testid={`pro-account-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold border ${tab === k ? "bg-[var(--oz-dark)] text-white border-[var(--oz-dark)]" : "border-[var(--oz-border)] text-[var(--oz-muted)]"}`}>{t}</button>
        ))}
      </div>
      <div className="mt-6">{tab === "profil" ? <Profile /> : tab === "devis" ? <Quotes /> : <Orders />}</div>
    </div>
  );
}
